// What the console calls an instrument's blocks (E-F12, roadmap P3.4).
//
// The console used to say "artifact" everywhere, because the first
// instrument assessed artifacts. A PMO run then showed its practice areas
// as "artifacts". The word now comes from the instrument: every block
// declares a structural `kind` (the compiler's closed vocabulary, see
// tools/validate_instrument.py BLOCK_KINDS), and when all blocks share one
// kind that reads as a noun, that noun is used. Anything else falls back to
// the neutral "area".
//
// BACKLOG §14.2 C2: no `if family ==`. The noun rides on declared content
// (block kind), never on the instrument's family, slug or title.
//
// Known limit (readiness-gate finding, not fixed here): no instrument
// declares a DISPLAY noun for its blocks; `kind` is a structural class and
// the table below maps it to a word. Adding a declared noun is a schema
// change.

export interface BlockNoun {
  /** "artifact" */
  one: string;
  /** "artifacts" */
  many: string;
}

/** Block kinds that read as a display noun. `dimension` is left out on
 *  purpose (it would collide with the criterion dimensions shown beside
 *  it), and `summary` is not a noun for a thing being assessed. */
const KIND_NOUN: Readonly<Record<string, BlockNoun>> = {
  artifact: { one: "artifact", many: "artifacts" },
  // Product owner 2026-10-17, option (b): `section` displays as "practice
  // area". A kind->word table entry, not a family check; it applies to any
  // instrument whose blocks are sections.
  section: { one: "practice area", many: "practice areas" },
  gate: { one: "gate", many: "gates" },
};

/** The neutral word when the instrument does not settle it. */
export const NEUTRAL_BLOCK_NOUN: BlockNoun = { one: "area", many: "areas" };

/** The noun for a set of blocks, from their declared kinds. Mixed kinds,
 *  unknown kinds or no blocks at all give the neutral noun. */
export function blockNoun(kinds: readonly string[]): BlockNoun {
  const distinct = new Set(kinds);
  if (distinct.size !== 1) return NEUTRAL_BLOCK_NOUN;
  const [only] = distinct;
  return KIND_NOUN[only as string] ?? NEUTRAL_BLOCK_NOUN;
}

/** "1 artifact", "3 artifacts", "0 areas". */
export function countOf(n: number, noun: BlockNoun): string {
  return `${n} ${n === 1 ? noun.one : noun.many}`;
}

/** "Artifact", "Sections". */
export function capitalised(word: string): string {
  return word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1);
}
